import { Box, Stack, Typography } from '@mui/joy';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { InfiniteScroll } from 'core/components';
import { Loader } from 'core/elements';
import { trimFilterText } from 'core/utils';
import { IPart } from 'parts/types';
import { Color, Level } from 'theme/enums';

import { LIST_HEADER_HEIGHT, LIST_HEIGHT, MIN_CHARS_TO_SEARCH } from './constants';
import { PartCard } from './PartCard';

interface IProps {
  records: IPart[];
  count: number;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isLoading: boolean;
  errorMessage?: string;
  selectedPart?: IPart | null;
  onPartInfo: (part: IPart) => void;
  isAlternateVersionEnabled?: boolean;
  query?: string;
  inputValue?: string;
  appliedFilters?: ReactNode;
}

export const PartList = ({
  records,
  count,
  hasNextPage,
  fetchNextPage,
  isLoading,
  errorMessage,
  selectedPart,
  onPartInfo,
  isAlternateVersionEnabled,
  query,
  inputValue,
  appliedFilters,
}: IProps) => {
  const { t } = useTranslation();

  // The search runs on the trimmed text: comparing the raw input would wait forever on spaces.
  const searchText = trimFilterText(inputValue) ?? '';
  const hasMinChars = searchText.length >= MIN_CHARS_TO_SEARCH;
  const isDebouncing = searchText !== (query ?? '');
  const showResults = hasMinChars && !isLoading && !isDebouncing;

  return (
    <Stack sx={{ gap: 1 }}>
      {showResults && (
        <Typography
          level={Level.BODY_SM}
          {...(errorMessage ? { color: Color.DANGER } : { sx: { color: 'neutral.500' } })}
        >
          {errorMessage || (count ? t('labelResults', { count }) : t('labelNoResults'))}
        </Typography>
      )}

      {appliedFilters}

      {!hasMinChars && (
        <Typography level={Level.BODY_SM} sx={{ color: 'neutral.500' }}>
          {t('messageSearchByCharsLength', { length: MIN_CHARS_TO_SEARCH })}
        </Typography>
      )}

      {hasMinChars && (isLoading || isDebouncing) && <Loader />}

      {showResults && (
        <Box>
          <InfiniteScroll
            data={records}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            height="fit-content"
            style={{
              overflowX: 'hidden',
              maxHeight: `calc(${LIST_HEIGHT} - ${LIST_HEADER_HEIGHT}px)`,
            }}
          >
            <Stack sx={{ gap: 1 }}>
              {records?.map((part) => (
                <PartCard
                  key={[part.id, part.manufacturerId, part.vendorNumber].filter(Boolean).join('-')}
                  part={part}
                  isSelected={
                    selectedPart?.number === part.number &&
                    selectedPart?.manufacturerId === part.manufacturerId
                  }
                  onPartInfo={onPartInfo}
                  isAlternateVersionEnabled={isAlternateVersionEnabled}
                />
              ))}
            </Stack>
          </InfiniteScroll>
        </Box>
      )}
    </Stack>
  );
};
